import React, { createContext, useCallback, useContext, useEffect, useState, useRef } from 'react';
import NotificationService from '../services/NotificationService';
import { useAuth } from './AuthContext';
import { Alert, Box, Card, Snackbar, Typography } from '@mui/material';
import { Info, CheckCircle, WarningAmber, Error as ErrorIcon } from '@mui/icons-material';
import {
  cacheNotifications,
  getCachedNotifications,
  getCachedUnreadCount,
  markAllCachedAsRead,
  setCachedUnreadCount,
  upsertNotification,
} from '../services/NotificationsCacheDb';

const NotificationContext = createContext({
  notifications: [],
  unreadCount: 0,
  loading: false,
  error: null,
  hasMore: true,
  fetchNotifications: () => {},
  markAllAsRead: () => {},
  createOptimisticNotification: () => {},
  showToast: () => {},
  hideToast: () => {},
});

class Notification {
  static _counter = 0;
  
  constructor(id, title, message, type, timestamp, read = false, icon = null, iconUrl = null) {
    this.id = id || `${Date.now()}_${++Notification._counter}`;
    this.title = title;
    this.message = message;
    this.type = type;
    this.timestamp = timestamp;
    this.read = read;
    this.icon = icon;
    this.iconUrl = iconUrl;
    this.display = true;
  }

  static new(title, message, type, timestamp, read = false, icon = null, iconUrl = null) {
    // Ensure unique ID by combining timestamp with incrementing counter
    const uniqueId = `${Date.now()}_${++Notification._counter}`;
    return new Notification(uniqueId, title, message, type, timestamp, read, icon, iconUrl);
  }
}

const PUSH_NOTIFICATION_DURATION = 5000; // ms

const getNotificationVisuals = (type) => {
  switch (type) {
    case 'success':
      return { Icon: CheckCircle, color: 'success.main', bg: 'success.light' };
    case 'warning':
      return { Icon: WarningAmber, color: 'warning.main', bg: 'warning.light' };
    case 'error':
      return { Icon: ErrorIcon, color: 'error.main', bg: 'error.light' };
    case 'info':
    default:
      return { Icon: Info, color: 'primary.main', bg: 'primary.light' };
  }
};

const formatTimeAgo = (timestamp) => {
  if (!timestamp) return '';

  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';

  const diffMs = Date.now() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  if (diffSeconds < 60) return `${diffSeconds || 1}s ago`;

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return `${diffMinutes}m ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
};

export const NotificationProvider = ({ children, notificationSocketService }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [toast, setToast] = useState(null);
  const [pushNotificationTurnOn, setPushNotificationTurnOn] = useState(true);
  const [pushNotifications, setPushNotifications] = useState([]);
  const limit = 20;
  const userId = user?.id;

  const optimisticIndexRef = useRef(new Map());
  const recentPushRef = useRef(new Map());

  const generateClientEventId = useCallback(() => {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }, []);

  const upsertByClientEventId = useCallback((clientEventId, next) => {
    if (!clientEventId) return;
    optimisticIndexRef.current.set(clientEventId, true);
    setNotifications((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      const idx = list.findIndex((n) => n?.metadata?.clientEventId === clientEventId);
      if (idx === -1) return [next, ...list];
      const clone = list.slice();
      clone[idx] = { ...clone[idx], ...next, metadata: { ...(clone[idx]?.metadata || {}), ...(next?.metadata || {}) } };
      return clone;
    });
  }, []);

  const removeByClientEventId = useCallback((clientEventId) => {
    if (!clientEventId) return;
    optimisticIndexRef.current.delete(clientEventId);
    setNotifications((prev) => (Array.isArray(prev) ? prev.filter((n) => n?.metadata?.clientEventId !== clientEventId) : []));
  }, []);

  const safeAsync = useCallback((fn) => {
    Promise.resolve()
      .then(fn)
      .catch(() => {
      });
  }, []);

  useEffect(() => {
   
    setPushNotificationTurnOn(true);
  }, []);

  useEffect(() => {
    if (!userId) return;

    safeAsync(async () => {
      const cachedUnread = await getCachedUnreadCount(userId);
      setUnreadCount(cachedUnread || 0);
      const cachedItems = await getCachedNotifications(userId);
      if (Array.isArray(cachedItems) && cachedItems.length > 0) {
        setNotifications(cachedItems);
      }
    });
  }, [userId, safeAsync]);

  // Fetch notifications from API
  const fetchNotifications = useCallback(async (pageNum = 1) => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      
      const response = await NotificationService.getNotifications(pageNum, limit);

      const list = Array.isArray(response)
        ? response
        : (Array.isArray(response?.data) ? response.data : (Array.isArray(response?.notifications) ? response.notifications : null));
      const meta = response?.meta || response?.pagination || null;
      const unreadFromServer = typeof response?.unreadNotifications === 'number' ? response.unreadNotifications : null;

      if (Array.isArray(list)) {
        if (pageNum === 1) {
          setNotifications(list);
        } else {
          setNotifications(prev => [...prev, ...list]);
        }

        setPage(meta?.currentPage || pageNum);
        setHasMore(meta ? meta.currentPage < meta.totalPages : list.length >= limit);

        const computedUnread = unreadFromServer !== null
          ? unreadFromServer
          : list.filter((n) => !(n?.isRead || n?.read)).length;
        setUnreadCount(computedUnread);

        safeAsync(async () => {
          const merged = pageNum === 1 ? list : [...(await getCachedNotifications(userId, 500)), ...list];
          await cacheNotifications(userId, merged);
          await setCachedUnreadCount(userId, computedUnread);
        });
      } else {
        console.warn('Unexpected notifications data format:', response);
        if (notifications.length === 0) {
          setNotifications([]);
          setHasMore(false);
        }
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(err.message || 'Failed to load notifications');
      if (notifications.length === 0) {
        setNotifications([]);
        setHasMore(false);
      }
    } finally {
      setLoading(false);
    }
  }, [user, limit, notifications.length, safeAsync, userId]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async (fromNotificationID) => {
    if (!fromNotificationID) return;

    try {
      await notificationSocketService.markAllAsRead(fromNotificationID);
      setUnreadCount(0);
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, isRead: true }))
      );

      safeAsync(async () => {
        await markAllCachedAsRead(userId);
      });
    } catch (err) {
      console.error('Error marking notifications as read:', err);
    }
  }, [safeAsync, userId, notificationSocketService]);

  // Toast notification functions
  const showToast = useCallback((message, duration = 3000) => {
    setToast(message);
    setTimeout(() => {
      setToast(null);
    }, duration);
  }, []);

  const hideToast = useCallback(() => {
    setToast(null);
  }, []);

  // Setup socket connection and listeners
  useEffect(() => {
    if (!user || !notificationSocketService) return;

    // Connect socket
    notificationSocketService.connect();

    // Fetch initial notifications
    fetchNotifications(1);

    // Listen for new notifications
    const notificationListener = notificationSocketService.addListener(
      'onNotificationReceived',
      (data) => {
        try {
          const newNotification = data.content;

          // setPushNotifications([
          //   Notification.new('Welcome!', 'Welcome to FigShelf!', 'info', new Date(), false),
          // ]);
          // Update state
          setNotifications(prev => [newNotification, ...prev]);
          setUnreadCount(prev => prev + 1);

          safeAsync(async () => {
            await upsertNotification(userId, newNotification);
            const cached = await getCachedUnreadCount(userId);
            await setCachedUnreadCount(userId, cached + 1);
          });
          
          // Show toast
          showToast(newNotification.message || newNotification.body || 'New notification');
        } catch (error) {
          console.error('❌ Error handling received notification:', error);
        }
      }
    );

    const optimisticPendingListener = notificationSocketService.addListener(
      'onOptimisticPending',
      (data) => {
        const payload = data?.content;
        const clientEventId = payload?.clientEventId;
        if (!clientEventId) return;
        upsertByClientEventId(clientEventId, {
          ...(payload?.notification || {}),
          metadata: { ...((payload?.notification && payload.notification.metadata) ? payload.notification.metadata : {}), clientEventId, optimisticStatus: 'pending' },
        });
      }
    );

    const optimisticConfirmedListener = notificationSocketService.addListener(
      'onOptimisticConfirmed',
      (data) => {
        const payload = data?.content;
        const clientEventId = payload?.clientEventId;
        const persisted = payload?.notification;
        if (!clientEventId || !persisted) return;

        setNotifications((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          const byId = list.findIndex((n) => n?.id === persisted?.id);
          if (byId !== -1) {
            const clone = list.slice();
            clone[byId] = persisted;
            return clone;
          }

          const byClientEvent = list.findIndex((n) => n?.metadata?.clientEventId === clientEventId);
          if (byClientEvent !== -1) {
            const clone = list.slice();
            clone[byClientEvent] = persisted;
            return clone;
          }

          return [persisted, ...list];
        });

        safeAsync(async () => {
          await upsertNotification(userId, persisted);
        });
      }
    );

    const optimisticFailedListener = notificationSocketService.addListener(
      'onOptimisticFailed',
      (data) => {
        const payload = data?.content;
        const clientEventId = payload?.clientEventId;
        if (!clientEventId) return;
        removeByClientEventId(clientEventId);
        showToast(payload?.error || 'Failed to create notification');
      }
    );

    const reconnectListener = notificationSocketService.on('reconnect', () => {
      fetchNotifications(1);
    });

    return () => {
      if (notificationSocketService) {
        notificationSocketService.removeListener('onNotificationReceived', notificationListener);
        notificationSocketService.removeListener('onOptimisticPending', optimisticPendingListener);
        notificationSocketService.removeListener('onOptimisticConfirmed', optimisticConfirmedListener);
        notificationSocketService.removeListener('onOptimisticFailed', optimisticFailedListener);
        try {
          reconnectListener && reconnectListener();
        } catch (_) {}
      }
    };
  }, [user, notificationSocketService, fetchNotifications, showToast]);

  const showPushNotification = useCallback((title, message, type = 'success', options = undefined) => {
    const icon = typeof options === 'string'
      ? options
      : (options && typeof options === 'object' ? options.icon : null);
    const iconUrl = options && typeof options === 'object' ? (options.iconUrl || null) : null;

    const fingerprint = `${type || ''}::${title || ''}::${message || ''}::${icon || ''}::${iconUrl || ''}`;
    const now = Date.now();
    const lastAt = recentPushRef.current.get(fingerprint);
    if (lastAt && now - lastAt < 2000) {
      return;
    }
    recentPushRef.current.set(fingerprint, now);

    // prevent unbounded growth
    if (recentPushRef.current.size > 200) {
      for (const [key, ts] of recentPushRef.current.entries()) {
        if (now - ts > 60000) recentPushRef.current.delete(key);
      }
    }

    const notification = Notification.new(title, message, type, new Date(), false, icon, iconUrl);
    try {
      console.log('[NotificationContext.showPushNotification]', {
        title,
        message,
        type,
        id: notification?.id,
        icon,
        iconUrl,
        at: new Date().toISOString(),
      });
    } catch (_) {}
    setPushNotifications(prev => [...prev, notification]);
  }, []);

  const value = {
    notifications,
    unreadCount,
    loading,
    error,
    hasMore,
    page,
    fetchNotifications,
    markAllAsRead,
    createOptimisticNotification: async ({ title, message, type = 'info', metadata = {}, userId: recipientUserId }) => {
      if (!notificationSocketService || !userId) return null;

      const clientEventId = generateClientEventId();
      const nowIso = new Date().toISOString();

      const optimistic = {
        id: `optimistic:${clientEventId}`,
        title,
        message,
        type,
        isRead: false,
        createdAt: nowIso,
        metadata: {
          ...(metadata || {}),
          clientEventId,
          optimisticStatus: 'pending',
        },
        user: recipientUserId ? { id: recipientUserId } : undefined,
      };

      upsertByClientEventId(clientEventId, optimistic);

      try {
        await notificationSocketService.createOptimisticNotification({
          clientEventId,
          notification: {
            title,
            message,
            type,
            userId: recipientUserId,
            metadata: { ...(metadata || {}), clientEventId },
          },
        });
      } catch (_) {
        removeByClientEventId(clientEventId);
        return null;
      }

      return clientEventId;
    },
    showToast,
    hideToast,
    toast,
    showPushNotification,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={hideToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={hideToast} severity="info" variant="filled" sx={{ width: '100%' }}>
          {typeof toast === 'string' ? toast : 'Notification'}
        </Alert>
      </Snackbar>

      <PushNotification
        enabled={pushNotificationTurnOn}
        notifications={pushNotifications}
        onConsume={(ids) => {
          if (!Array.isArray(ids) || ids.length === 0) return;
          setPushNotifications((prev) => (prev || []).filter((n) => !ids.includes(n.id)));
        }}
      />
    </NotificationContext.Provider>
  );
};

const PushNotification = ({ enabled, notifications, onConsume }) => {
  const [items, setItems] = useState([]);
  const timersRef = useRef({});

  useEffect(() => {
    if (!enabled || !notifications || notifications.length === 0) return;

    try {
      console.log('[PushNotification] incoming notifications', {
        enabled,
        notificationsCount: notifications.length,
        currentItemsCount: items.length,
        at: new Date().toISOString(),
      });
    } catch (_) {}

    let consumedIds = [];
    setItems((prev) => {
      const existingIds = new Set(prev.map((n) => n.id));
      const now = Date.now();
      const newOnes = notifications
        .filter((n) => !existingIds.has(n.id))
        .map((n) => ({
          ...n,
          remaining: PUSH_NOTIFICATION_DURATION,
          startedAt: now,
          isPaused: false,
        }));

      if (newOnes.length === 0) return prev;
      consumedIds = newOnes.map((n) => n.id);
      return [...newOnes, ...prev];
    });

    if (consumedIds.length > 0 && typeof onConsume === 'function') {
      try {
        onConsume(consumedIds);
      } catch (_) {}
    }
  }, [enabled, notifications, items.length]);

  useEffect(() => {
    if (!enabled || items.length === 0) return;

    items.forEach((item) => {
      if (item.isPaused || timersRef.current[item.id]) return;

      const delay = item.remaining ?? PUSH_NOTIFICATION_DURATION;

      timersRef.current[item.id] = setTimeout(() => {
        setItems((prev) => prev.filter((n) => n.id !== item.id));
        delete timersRef.current[item.id];
      }, delay);
    });

    return () => {
      Object.values(timersRef.current).forEach((timeoutId) => clearTimeout(timeoutId));
      timersRef.current = {};
    };
  }, [enabled, items]);

  const handleMouseEnter = (id) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== id || item.isPaused) return item;

        const timerId = timersRef.current[id];
        if (timerId) {
          clearTimeout(timerId);
          delete timersRef.current[id];
        }

        const now = Date.now();
        const baseRemaining = item.remaining ?? PUSH_NOTIFICATION_DURATION;
        const elapsed = item.startedAt ? now - item.startedAt : 0;
        const remaining = Math.max(baseRemaining - elapsed, 0);

        return {
          ...item,
          remaining,
          isPaused: true,
          startedAt: null,
        };
      })
    );
  };

  const handleMouseLeave = (id) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== id || !item.isPaused) return item;

        return {
          ...item,
          isPaused: false,
          startedAt: Date.now(),
        };
      })
    );
  };

  if (!enabled || items.length === 0) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 40,
        left: 40,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      {items.map((notification) => {
        const visuals = getNotificationVisuals(notification.type);
        const IconComponent = visuals.Icon;

        return (
          <Card
            key={notification.id}
            sx={{
              p: 2,
              boxShadow: 3,
              width: 320,
              maxWidth: '90vw',
              minHeight: 80,
              display: 'flex',
              gap: 1.5,
              alignItems: 'flex-start',
            }}
            onMouseEnter={() => handleMouseEnter(notification.id)}
            onMouseLeave={() => handleMouseLeave(notification.id)}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: visuals.bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {notification.iconUrl ? (
                <Box
                  component="img"
                  src={notification.iconUrl}
                  alt=""
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              ) : notification.icon ? (
                <Typography sx={{ color: visuals.color, fontSize: 20, lineHeight: 1 }}>
                  {notification.icon}
                </Typography>
              ) : (
                <IconComponent sx={{ color: visuals.color, fontSize: 22 }} />
              )}
            </Box>

            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  mb: 0.5,
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold" noWrap>
                  {notification.title || 'Notification'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {formatTimeAgo(notification.timestamp)}
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary">
                {notification.message}
              </Typography>

              <Box sx={{ mt: 1.5 }}>
                <Box
                  sx={{
                    position: 'relative',
                    height: 3,
                    borderRadius: 999,
                    bgcolor: 'action.hover',
                    overflow: 'hidden',
                    '@keyframes shrinkBar': {
                      from: { transform: 'translateX(0%)' },
                      to: { transform: 'translateX(-100%)' },
                    },
                    '::after': {
                      content: '""',
                      position: 'absolute',
                      inset: 0,
                      bgcolor: visuals.color,
                      animation: `shrinkBar ${PUSH_NOTIFICATION_DURATION}ms linear forwards`,
                      animationPlayState: notification.isPaused ? 'paused' : 'running',
                    },
                  }}
                />
              </Box>
            </Box>
          </Card>
        );
      })}
    </Box>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext;
